import { snowBracketCombinationStrength } from 'utils/snowBracketString';
import {
  getArrayWithoutEmptyStrings,
  getIsAllArrayInValid,
  getIsAnyValueFalseOrInfinity,
  getIsAnyValueFalseOrInfinityExceptZero,
  getIsInfinityOrError
} from 'utils/utils';

let reqSnowBrktPerSqr;

// safetyFacor
export const getSafetyfactor = (roofType) => {
  if (
    roofType === 'Asphalt Shingles' ||
    roofType === 'Wood Shakes' ||
    roofType === 'Granule Metal' ||
    roofType === 'Cedar Shingles'
  ) {
    return 2.1;
  } else {
    return 2.47;
  }
};

// bracket strength
export const getBrktStrength = (combination) => {
  return parseFloat(
    snowBracketCombinationStrength.find((item) => item.combination === combination)?.strength
  );
};

export const getSnowBrktPerSqr = (
  grndSnowLoad,
  rainLoad,
  roofSlope,
  brktStrength,
  safetyFactor
) => {
  return (
    ((grndSnowLoad + rainLoad) *
      Math.cos(Math.atan(roofSlope / 12)) *
      100 *
      Math.sin(Math.atan(roofSlope / 12))) /
    (brktStrength / safetyFactor)
  );
};

// actualSnowBracketPerSquare
export const getActualSnowBrktPerSqr = (horizontalEaveSpacing, verticalSpacing) => {
  if (getIsAnyValueFalseOrInfinity([horizontalEaveSpacing, verticalSpacing])) return 'error';
  return ((120 / horizontalEaveSpacing) * 120) / verticalSpacing;
};

// horizontalEaveSpacing
export const getHorizontalEaveSpacing = (horizontalFieldSpacing) => {
  if (getIsAnyValueFalseOrInfinityExceptZero([horizontalFieldSpacing])) return 'error';
  return (horizontalFieldSpacing / 2).toFixed(3);
};

// numOfCoursesVertically
// numOfCoursesHorizontalAtEave
export const getNumOfCourses = (spacing, exposure) => {
  if (getIsAnyValueFalseOrInfinity([exposure])) return 'error';
  const numOfCourses =
    spacing < Math.floor(exposure / 0.125) * 0.125
      ? spacing / exposure
      : Math.round(spacing / exposure);
  return numOfCourses.toFixed(2);
};

// total brackets : to be updated
export const getTotalNumOfBracket = (roofingSqrs, noOfbrkts) => {
  return Math.ceil(roofingSqrs) * Math.ceil(noOfbrkts);
};

//************************ C *****************************/

//H8 ==C32=C36=C37=C38
//C33=C34=C35
export const calC33 = (horizontalExposure) => {
  const h8 = calH8(horizontalExposure);
  // console.log('h8', h8);
  if (getIsInfinityOrError([h8])) return 'error';
  return h8 - horizontalExposure;
};

// C39=C40=C41
const calC39 = (horizontalExposure) => {
  const h8 = calH8(horizontalExposure);
  if (getIsInfinityOrError([h8])) return 'error';
  return h8 + horizontalExposure;
};
//

//************************ D *****************************/
//calculation D3
export const calD3 = (roofType) => getSafetyfactor(roofType);

//D32=D34=D37=D40
export const calD32 = (verticalExposure) => {
  if (getIsAnyValueFalseOrInfinity([verticalExposure, reqSnowBrktPerSqr])) return 'error';
  return Math.round(120 / (Math.sqrt(reqSnowBrktPerSqr) * verticalExposure)) * verticalExposure;
};
//D33=D36=D39
export const calD33 = (verticalExposure) => {
  if (getIsAnyValueFalseOrInfinity([verticalExposure])) return 'error';
  const d32 = calD32(verticalExposure);
  if (getIsInfinityOrError([d32])) return 'error';
  return d32 - verticalExposure > 0 ? d32 - verticalExposure : d32 + 2 * verticalExposure;
};

//D35=D38=D41
export const calD35 = (verticalExposure) => {
  const d32 = calD32(verticalExposure);
  if (getIsInfinityOrError([d32])) return 'error';
  return d32 + verticalExposure;
};

//************************ E *****************************/
//E32=H6
export const calE33 = (horizontalExposure, verticalExposure) => {
  const c33 = calC33(horizontalExposure);
  if (getIsInfinityOrError([c33])) return 'error';
  if (c33 >= 30) return 0;
  const d33 = calD33(verticalExposure);
  if (getIsInfinityOrError([d33])) return 'error';
  return ((120 / c33) * 120) / d33;
};

// getting arrays of c and d cells
const getCnDArrays = (horizontalExposure, verticalExposure) => {
  // c33 equals to c34, c35, c39, c40, c41
  const c33 = calC33(horizontalExposure);

  // c32 equals to c36, c37, c38, h8
  const c32 = calH8(horizontalExposure);

  // c39
  const c39 = calC39(horizontalExposure);

  // D32 equals to d34, d37, d40
  const d34 = calD32(verticalExposure);

  // d33 equals to d36, d39
  const d33 = calD33(verticalExposure);

  // d35 equals to d38, d41
  const d35 = calD35(verticalExposure);

  const cArray = [];
  const dArray = [];

  for (let i = 33; i <= 41; i++) {
    let c, d;
    // getting c // set to Infinity if error
    if ([33, 34, 35].includes(i)) c = c33 === 'error' ? Infinity : c33;
    else if ([39, 40, 41].includes(i)) c = c39 === 'error' ? Infinity : c39;
    else if ([36, 37, 38].includes(i)) c = c32 === 'error' ? Infinity : c32;

    // getting d
    if ([33, 36, 39].includes(i)) d = d33 === 'error' ? Infinity : d33;
    else if ([34, 37, 40].includes(i)) d = d34 === 'error' ? Infinity : d34;
    else if ([35, 38, 41].includes(i)) d = d35 === 'error' ? Infinity : d35;

    cArray.push(c);
    dArray.push(d);
  }
  // console.log('cArray', cArray, 'dArray', dArray);
  return [cArray, dArray];
};

// E40
export const calE40 = (horizontalExposure, verticalExposure) => {
  const c40 = calC39(horizontalExposure);
  const d40 = calD32(verticalExposure);
  if (getIsInfinityOrError([c40])) return 'error';
  if (c40 < 30 && getIsInfinityOrError([d40])) return 'error';
  return c40 < 30 ? (120 / c40) * (120 / d40) : 0;
};

// E33 to E41
export const calE33To41 = (horizontalExposure, verticalExposure) => {
  const [cArray, dArray] = getCnDArrays(horizontalExposure, verticalExposure);
  if (getIsAllArrayInValid(cArray)) return ['error'];
  // console.log('Cs n Ds', cArray, dArray);
  // console.log(
  //   'calE33To41',
  //   cArray?.map((c, i) => (c < 30 ? ((120 / c) * 120) / dArray[i] : 0))
  // );
  return cArray?.map((c, i) => (c < 30 ? ((120 / c) * 120) / dArray[i] : 0));
};

//grandSnowLoad is from sheet interface H12
//rainLoad is from sheet interface H13

//************************ F *****************************/
//CalF3
export const calF3 = (grndSnowLoad, rainLoad) => grndSnowLoad + rainLoad;

//F33
export const calF33 = (horizontalExposure, verticalExposure) => {
  const e33 = calE33(horizontalExposure, verticalExposure);
  if (getIsInfinityOrError([e33])) return 'error';
  return e33 - reqSnowBrktPerSqr;
};

// F40
export const calF40 = (horizontalExposure, verticalExposure) => {
  const e40 = calE40(horizontalExposure, verticalExposure);
  if (getIsInfinityOrError([e40])) return 'error';
  return e40 - reqSnowBrktPerSqr;
};

// F33  to F41
export const calF33ToF41 = (horizontalExposure, verticalExposure) => {
  const e33_e41 = calE33To41(horizontalExposure, verticalExposure);
  // console.log('Es', e33_e41);
  if (getIsAllArrayInValid(e33_e41)) return ['error'];
  const e32 = reqSnowBrktPerSqr;
  return e33_e41?.map((val) => val - e32);
};
//************************ G *****************************/
//G3 is vertical exposure
//G33
export const calG33 = (horizontalExposure, verticalExposure) => {
  const f33 = calF33(horizontalExposure, verticalExposure);
  if (getIsInfinityOrError([f33])) return 'error';
  return f33 < 0 ? '' : f33;
};

//G40
export const calG40 = (horizontalExposure, verticalExposure) => {
  const f40 = calF40(horizontalExposure, verticalExposure);
  if (getIsInfinityOrError([f40])) return 'error';
  return f40 < 0 ? 0 : f40;
};

// G33 to G41
export const calG33ToG41 = (horizontalExposure, verticalExposure) => {
  const f33_f41 = calF33ToF41(horizontalExposure, verticalExposure);
  // console.log('Fs 3341', f33_f41);
  if (getIsAllArrayInValid(f33_f41)) return ['error'];
  return f33_f41?.map((val) => (val < 0 ? '' : val));
};

// G42
export const calG42 = (horizontalExposure, verticalExposure) => {
  const g33_g41 = calG33ToG41(horizontalExposure, verticalExposure);
  // console.log('g33_g41', g33_g41);
  if (getIsInfinityOrError(g33_g41)) return 'error';
  return Math.min(...getArrayWithoutEmptyStrings(g33_g41));
};
//************************ H *****************************/
// H8 ==C32=C36=C37=C38
export const calH8 = (horizontalExposure) => {
  if (getIsAnyValueFalseOrInfinity([horizontalExposure, reqSnowBrktPerSqr])) return 'error';
  return (
    Math.round(120 / (Math.sqrt(reqSnowBrktPerSqr) * horizontalExposure * 2)) *
    (horizontalExposure * 2)
  );
};

//H10
export const calH10 = (horizontalExposure) => {
  if (horizontalExposure * 2 > 30) {
    const i2_i6 = [];
    for (let i = 1; i <= 5; i++) {
      i2_i6.push(calI2ToI6(horizontalExposure, i));
      // console.log('i2-i6', i2_i6);
    }
    if (getIsInfinityOrError(i2_i6)) return 'error';
    return Math.max(...getArrayWithoutEmptyStrings(i2_i6));
  } else {
    const j3_j15 = [];
    for (let i = 3; i <= 15; i = i + 2) {
      j3_j15.push(calJ3ToJ15(horizontalExposure, i));
    }
    // console.log('j3-j15', j3_j15);
    if (getIsInfinityOrError(j3_j15)) return 'error';
    return Math.max(...getArrayWithoutEmptyStrings(j3_j15));
  }
};

//H11
export const calH11 = (horizontalExposure) => {
  const h8 = calH8(horizontalExposure);
  // console.log('calH8', calH8(horizontalExposure));
  // console.log('h10', calH10(horizontalExposure));
  if (getIsInfinityOrError([h8])) return 'error';
  const h10 = calH10(horizontalExposure);
  if (h8 > 30 && getIsInfinityOrError([h10])) return 'error';

  return h8 > 30 ? h10 : h8;
};

// H12
export const calH12 = (horizontalExposure) => {
  if (getIsAnyValueFalseOrInfinity([reqSnowBrktPerSqr])) return 'error';
  const h11 = calH11(horizontalExposure);
  if (getIsInfinityOrError([h11])) return 'error';
  return (120 * 120) / (h11 * reqSnowBrktPerSqr);
};

//H15
const calH15 = (verticalExposure) => {
  // console.log('calH15', (120 * 120) / (calH16(verticalExposure) * reqSnowBrktPerSqr));
  if (getIsAnyValueFalseOrInfinity([reqSnowBrktPerSqr])) return 'error';
  const h16 = calH16(verticalExposure);
  if (getIsInfinityOrError([h16])) return 'error';
  return (120 * 120) / (h16 * reqSnowBrktPerSqr);
};
//H16
const calH16 = (verticalExposure) => {
  if (getIsAnyValueFalseOrInfinity([reqSnowBrktPerSqr, verticalExposure])) return 'error';
  return Math.round(120 / Math.sqrt(reqSnowBrktPerSqr) / verticalExposure) * verticalExposure;
};

//H17
export const calH17 = (verticalExposure) => {
  if (getIsAnyValueFalseOrInfinity([reqSnowBrktPerSqr])) return 'error';
  const h18 = calH18(verticalExposure);
  if (getIsInfinityOrError([h18])) return 'error';
  return ((120 * 120) / h18) * reqSnowBrktPerSqr;
};

//H18
export const calH18 = (verticalExposure) => {
  if (getIsAnyValueFalseOrInfinityExceptZero([reqSnowBrktPerSqr, verticalExposure])) return 'error';
  return (
    Math.round(120 / (Math.sqrt(reqSnowBrktPerSqr) * verticalExposure)) * verticalExposure +
    verticalExposure
  );
};

//H19
export const calH19 = (verticalExposure) => {
  // console.log('vexp', verticalExposure);
  if (getIsAnyValueFalseOrInfinity([reqSnowBrktPerSqr])) return 'error';
  const h20 = calH20(verticalExposure);
  // console.log('calh20', h20);
  if (getIsInfinityOrError([h20])) return 'error';
  // console.log('calh20 * req', h20 * reqSnowBrktPerSqr);
  return (120 * 120) / (h20 * reqSnowBrktPerSqr);
};

//H20
export const calH20 = (verticalExposure) => {
  const i19ToX19 = [];
  const i20ToX20 = [];
  for (let i = 1; i <= 16; i++) {
    const val18 = calH18ToW18(verticalExposure, i);
    const val17 = (120 * 120) / (val18 * reqSnowBrktPerSqr);
    // console.log('val17', val17);
    if (val17 < 30) {
      i19ToX19.push(val17);
      i20ToX20.push(val18);
    }
  }

  const maxFromI19ToX19 = Math.max(...getArrayWithoutEmptyStrings(i19ToX19));
  const minFromI20ToX20 = Math.min(...getArrayWithoutEmptyStrings(i20ToX20));

  const h15 = calH15(verticalExposure);

  // console.log(
  //   'h15',
  //   h15,
  //   'minFromI20ToX20',
  //   minFromI20ToX20,
  //   'maxFromI19ToX19',
  //   maxFromI19ToX19,
  //   'array',
  //   ...getArrayWithoutEmptyStrings(i19ToX19)
  // );

  if (getIsInfinityOrError([h15])) return 'error';

  if (h15 < 30) {
    const h16 = calH16(verticalExposure);
    // console.log('h16', h16);
    if (getIsInfinityOrError([h16])) return 'error';
    return calH16(verticalExposure);
  } else {
    if (getIsInfinityOrError([maxFromI19ToX19])) return 'error';
    if (maxFromI19ToX19 === 0) {
      return (120 * 120) / (30 * reqSnowBrktPerSqr);
    } else {
      return getIsInfinityOrError([minFromI20ToX20]) ? 'error' : minFromI20ToX20;
    }
  }
};

//************************ combination of H & W *****************************/
//H18 to W18
export const calH18ToW18 = (verticalExposure, num) => {
  if (getIsAnyValueFalseOrInfinity([reqSnowBrktPerSqr, verticalExposure])) return ['error'];
  return (
    Math.round(120 / (Math.sqrt(reqSnowBrktPerSqr) * verticalExposure)) * verticalExposure +
    num * verticalExposure
  );
};

//H17 to W17
// export const calH17ToW17 = (func, verticalExposure) => {
//   return ((120 * 120) / func(verticalExposure)) * reqSnowBrktPerSqr;
// };

// H23 = H24
export const calH23 = () => {
  if (getIsAnyValueFalseOrInfinity([reqSnowBrktPerSqr])) return 'error';
  return Math.floor(Math.sqrt(14400 / reqSnowBrktPerSqr) / 0.25) * 0.25;
};

//H25
export const calH25 = () => {
  const h23 = calH23();
  if (getIsInfinityOrError([h23])) return 'error';
  // console.log('h23', h23);
  return h23 > 30 ? 30 : h23;
};

// H26
export const calH26 = () => {
  const h25 = calH25();
  // h23 = h24
  const h23 = calH23();
  if (getIsInfinityOrError([h25, h23])) return 'error';
  if (getIsAnyValueFalseOrInfinity([reqSnowBrktPerSqr])) return 'error';
  return h25 === h23 ? h23 : (120 * 120) / (h25 * reqSnowBrktPerSqr);
};

//H33
export const calH33 = (horizontalExposure, verticalExposure) => {
  const calG33 = calG33(horizontalExposure, verticalExposure);
  const calG42 = calG42(horizontalExposure, verticalExposure);

  if (getIsInfinityOrError([calG33, calG42])) return 'error';
  if (calG33 != calG42) return '';

  const calC33 = calC33(horizontalExposure, verticalExposure);
  const calD33 = calD33(horizontalExposure, verticalExposure);

  if (getIsInfinityOrError([calC33, calD33])) return 'error';
  return Math.abs(calC33 - calD33);
};

//H40
export const calH40 = (horizontalExposure, verticalExposure) => {
  const calG40 = calG40(horizontalExposure, verticalExposure);
  const calG42 = calG42(horizontalExposure, verticalExposure);

  if (getIsInfinityOrError([calG40, calG42])) return 'error';
  if (calG40 != calG42) return '';

  const calC39 = calC39(horizontalExposure);
  const calD32 = calD32(horizontalExposure);

  if (getIsInfinityOrError([calC39, calD32])) return 'error';
  return Math.abs(calC39 - calD32);
};

//H33 To H41
export const calH33ToH41 = (horizontalExposure, verticalExposure) => {
  const [cArray, dArray] = getCnDArrays(horizontalExposure, verticalExposure);
  const g33_g41 = calG33ToG41(horizontalExposure, verticalExposure);
  // console.log('Gs', g33_g41);
  if (getIsAllArrayInValid(g33_g41)) return ['error'];
  const g42 = calG42(horizontalExposure, verticalExposure);
  // console.log('h ki range', g33_g41, calG42(horizontalExposure, verticalExposure));
  // console.log(
  //   'h ki range filtered',
  //   g33_g41.filter((iVal) => iVal !== '')
  // );
  // console.log('g42', g42);
  if (getIsInfinityOrError([g42])) return ['error'];
  return g33_g41?.map((g, i) => (g === g42 ? Math.abs(cArray[i] - dArray[i]) : ''));
};

// H42
export const calH42 = (horizontalExposure, verticalExposure) => {
  const h33_h41 = calH33ToH41(horizontalExposure, verticalExposure);
  if (getIsInfinityOrError(h33_h41)) return 'error';
  return Math.min(...getArrayWithoutEmptyStrings(h33_h41));
};

//************************ I *****************************/
//I2 to I6
export const calI2ToI6 = (horizontalExposure, k) => {
  if (!horizontalExposure) return ['error'];
  return horizontalExposure / k < 30 ? horizontalExposure / k : 0;
};

//I19
export const calI19 = (verticalExposure) => {
  const h17 = calH17(verticalExposure);
  if (getIsInfinityOrError([h17])) return 'error';
  return h17 < 30 ? h17 : 0;
};

//I20
export const calI20 = (verticalExposure) => {
  const h17 = calH17(verticalExposure);
  if (getIsInfinityOrError([h17])) return 'error';
  if (h17 >= 30) return '';
  const h18 = calH18(verticalExposure);
  if (getIsInfinityOrError([h18])) return 'error';
  return h18;
};

// I41

// I33 to I41
export const calI33ToI41 = (horizontalExposure, verticalExposure) => {
  const h33_h41 = calH33ToH41(horizontalExposure, verticalExposure);
  // console.log('h ki range', h33_h41);
  // console.log(
  //   'h ki range filtered',
  //   h33_h41.filter((iVal) => iVal !== '')
  // );
  const cArray = getCnDArrays(horizontalExposure, verticalExposure)[0];
  const h42 = calH42(horizontalExposure, verticalExposure);
  if (getIsInfinityOrError([h42]) || getIsAllArrayInValid(h33_h41)) return ['error'];
  return h33_h41?.map((h, i) => (h === h42 ? cArray[i] : ''));
};

// I42
export const calI42 = (horizontalExposure, verticalExposure) => {
  const i33_i41 = calI33ToI41(horizontalExposure, verticalExposure);
  if (getIsInfinityOrError(i33_i41)) return 'error';
  // console.log('I ki range', i33_i41);
  // console.log(
  //   'I ki range filtered',
  //   i33_i41.filter((val) => val !== '')
  // );
  return Math.min(...getArrayWithoutEmptyStrings(i33_i41));
};
// I43
export const calI43 = (horizontalExposure, verticalExposure) => {
  const i42 = calI42(horizontalExposure, verticalExposure);
  // console.log('i42', i42);
  if (getIsInfinityOrError([i42])) return 'error';
  if (i42 != 0) return i42;
  const h11 = calH11(horizontalExposure);
  // console.log('h11', h11);
  if (getIsInfinityOrError([h11])) return 'error';
  // console.log('i42', i42);
  console.log('h11', h11);
  return h11;
};
// it only take odd values of k 3 to 15
export const calJ3ToJ15 = (horizontalExposure, k) => {
  if (!horizontalExposure) return ['error'];
  return horizontalExposure * (k - 1) < 30 ? horizontalExposure * (k - 1) : 0;
};

// J33 to J41
export const calJ33ToJ41 = (horizontalExposure, verticalExposure) => {
  const d33_d41 = getCnDArrays(horizontalExposure, verticalExposure)[1];
  // console.log(d33_d41, 'Ds');
  if (getIsAllArrayInValid(d33_d41)) return ['error'];
  const h33_h41 = calH33ToH41(horizontalExposure, verticalExposure);
  // console.log(h33_h41, 'Hs');
  if (getIsAllArrayInValid(h33_h41)) return ['error'];
  const h42 = calH42(horizontalExposure, verticalExposure);
  if (getIsInfinityOrError([h42])) return ['error'];
  return h33_h41?.map((h, i) => (h === h42 ? d33_d41[i] : ''));
};

// J42
export const calJ42 = (horizontalExposure, verticalExposure) => {
  const j33_j41 = calJ33ToJ41(horizontalExposure, verticalExposure);
  // console.log('j33_j41', j33_j41);
  if (getIsInfinityOrError(j33_j41)) return 'error';
  return Math.min(...getArrayWithoutEmptyStrings(j33_j41));
};

// J43
export const calJ43 = (horizontalExposure, verticalExposure) => {
  const j42 = calJ42(horizontalExposure, verticalExposure);
  // console.log('calJ42', j42);
  if (getIsInfinityOrError([j42])) return 'error';
  // console.log('i43', calI43(horizontalExposure, verticalExposure));
  if (j42 != 0) return j42;
  const i43 = calI43(horizontalExposure, verticalExposure);
  if (getIsInfinityOrError([i43])) return 'error';
  if (!horizontalExposure || !verticalExposure || !reqSnowBrktPerSqr) return 'error';
  return (
    Math.floor((120 * 120) / (i43 * reqSnowBrktPerSqr) / verticalExposure / 1) *
    1 *
    verticalExposure
  );
};

//************************ W *****************************/
// W17
export const calW17 = (verticalExposure) => {
  if (getIsAnyValueFalseOrInfinity([reqSnowBrktPerSqr])) return 'error';
  const w18 = calW18(verticalExposure);
  if (getIsInfinityOrError([w18])) return 'error';
  return (120 * 120) / (w18 * reqSnowBrktPerSqr);
};

//W18
export const calW18 = (verticalExposure) => {
  if (getIsAnyValueFalseOrInfinity([reqSnowBrktPerSqr, verticalExposure])) return 'error';
  return (Math.round(120 / Math.sqrt(reqSnowBrktPerSqr)) / verticalExposure) * verticalExposure;
};

//************************ X *****************************/
//X19
export const calX19 = (verticalExposure) => {
  const w17 = calW17(verticalExposure);
  if (getIsInfinityOrError([w17])) return 'error';
  return w17 < 30 ? w17 : 0;
};

//X20
export const calX20 = (verticalExposure) => {
  const w17 = calW17(verticalExposure);
  if (getIsInfinityOrError([w17])) return 'error';
  if (w17 <= 30) return '';
  const w18 = calW18(verticalExposure);
  if (getIsInfinityOrError([w18])) return 'error';
  return w18;
};

const getResult = (result, value) =>
  result == 'error' ? result : Math.floor(result / value) * value;

// main method called from main calculator page (interface sheet)
export const getHorizontalFieldSpacing = (
  horizontalExposure,
  verticalExposure,
  reqSnowBrktPerSqrArg,
  useHorizontal,
  useVertical,
  useBoth,
  useNoRestrictions
) => {
  reqSnowBrktPerSqr = reqSnowBrktPerSqrArg;

  const result = useHorizontal
    ? calH11(horizontalExposure)
    : useVertical
      ? calH19(verticalExposure)
      : useBoth
        ? calI43(horizontalExposure, verticalExposure)
        : useNoRestrictions
          ? calH25()
          : 'error';
  return getResult(result, 0.25);
};

// vertical spacing main method
export const getVerticalSpacing = (
  horizontalExposure,
  verticalExposure,
  reqSnowBrktPerSqrArg,
  useHorizontal,
  useVertical,
  useBoth,
  useNoRestrictions
) => {
  reqSnowBrktPerSqr = reqSnowBrktPerSqrArg;
  const result = useHorizontal
    ? calH12(horizontalExposure)
    : useVertical
      ? calH20(verticalExposure)
      : useBoth
        ? calJ43(horizontalExposure, verticalExposure)
        : useNoRestrictions
          ? calH26()
          : 'error';
  return getResult(result, 0.125);
};
